import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconoirCheckboxControl } from '../IconoirCheckboxControl';
import {
  DATA_USE_CONSENT_CHECKBOX_LABEL,
  DATA_USE_CONSENT_TAGLINE_DEFAULT,
} from '../../../features/flow/utils';

export type DataUseConsentCopy = {
  tagline: string;
  bodyParagraphs: string[];
};

function isAllCapsLine(text: string): boolean {
  const t = text.trim();
  if (!t || !/[A-Za-z]/.test(t)) return false;
  return t === t.toUpperCase();
}

function consentBodyParagraphClassName(text: string): string {
  const trimmed = text.trim();
  const allCaps = isAllCapsLine(text);
  const underlined = trimmed === 'Further Information' || text.includes('?');
  return [
    'text-[13px] text-[#525353]',
    allCaps ? 'text-center' : '',
    underlined ? 'underline' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function DataUseConsentScreenBody({
  title,
  copy,
  agreed,
  onToggleAgreed,
}: {
  title: string;
  copy: DataUseConsentCopy;
  agreed: boolean;
  onToggleAgreed: () => void;
}) {
  const { t } = useTranslation('common');

  return (
    <div className="w-full max-w-[640px] flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-[#131315]">{title}</h1>
      <p className="text-[#131315] text-sm leading-relaxed">
        {t('data_use_consent.plain_summary', {
          defaultValue:
            "In short: we only use your data to prepare your tax return. We don't sell it. You can ask us to delete it at any time. The full legal detail is below.",
        })}
      </p>
      <p className="text-[#131315] text-sm font-medium">{t('data_use_consent.tagline', { defaultValue: copy.tagline })}</p>
      <div className="rounded-lg border border-[#E2E8F0] bg-white p-4 h-[480px] w-full max-w-[620px] overflow-y-auto shadow-[0_2px_6px_0_rgba(34,34,34,0.10)]">
        <div className="space-y-3 leading-relaxed">
          {copy.bodyParagraphs.length > 0 ? (
            copy.bodyParagraphs.map((p, i) => (
              <p key={i} className={consentBodyParagraphClassName(p)}>
                {p}
              </p>
            ))
          ) : (
            <p className="text-[13px] text-[#525353]">{t('data_use_consent.tagline', { defaultValue: DATA_USE_CONSENT_TAGLINE_DEFAULT })}</p>
          )}
        </div>
      </div>
      <label className="flex items-start gap-3 cursor-pointer text-sm text-[#131315] font-medium">
        <span className="mt-0.5 inline-flex items-center">
          <IconoirCheckboxControl
            checked={agreed}
            onChange={() => {
              onToggleAgreed();
            }}
            size="md"
          />
        </span>
        <span className="text-[#131315] text-sm font-medium">{t('data_use_consent.checkbox_label', { defaultValue: DATA_USE_CONSENT_CHECKBOX_LABEL })}</span>
      </label>
    </div>
  );
}
